import type { FormFieldView } from '@tabel/shared/form-fields';
import { fieldVisible } from '@tabel/shared/form-fields';
import type { RuleField, RuleGroupView, RulesView } from '@tabel/shared/schemas/rules';
import { type FieldIndex, type Values, sameValue } from './answer';
import { T, say } from './texts';

/*
 * Изменение правила на месте (004, T063; contracts/ui.md, «Карточка правила» → «Изменение»): что вводит человек,
 * проверка поля при вводе и блоки правила для записи в черновик и для расчёта примера и влияния. Проверка здесь —
 * только формы поля (число, время, пределы схемы); проверку набора — закон, связки, запреты между правилами —
 * делает сервер (`errors` у поля, FR-024). Часы здесь не считаются.
 */

/** Ключ поля правила: `кубик.поле`. */
export const keyOf = (f: Pick<RuleField, 'cube' | 'name'>) => `${f.cube}.${f.name}`;

/**
 * Поле правила, которое человек меняет: не закон, правило — не закон и не правило системы, расчёт поле читает
 * (`unused` — «Расчёт пока это не учитывает», 16.10.2026).
 */
export function editable(g: RuleGroupView, f: RuleField | undefined): boolean {
  return (
    f !== undefined &&
    !f.locked &&
    f.unused !== true &&
    f.teamOnly !== true &&
    !g.law &&
    !g.fixed &&
    g.fields.some((x) => keyOf(x) === keyOf(f))
  );
}

/**
 * У правила есть что изменить: хотя бы одно поле, которое человек меняет, и у выбора — два варианта и больше.
 * Нет — кнопки «Изменить» нет (приёмка 16.10.2026: «В какие строки табеля ставить часы?» открывалось пустым).
 */
export function canChange(g: RuleGroupView): boolean {
  return g.fields.some((f) => editable(g, f) && (f.control !== 'select' || (f.options?.length ?? 0) >= 2));
}

/** Поле вводится текстом: число, время, строка. */
export const typed = (f: RuleField) =>
  f.control === 'integer' || f.control === 'number' || f.control === 'text';

/** Время суток «ЧЧ:ММ» (`timeKind: время_суток`). */
export const isTime = (f: RuleField) => f.control === 'text' && f.timeKind === 'время_суток';

/**
 * Во сколько раз единица ввода больше единицы поля. Поле в секундах (точность округления, наименьшие часы за дату,
 * граница округления) человек видит и вводит в минутах (решение координатора 004, 16.10.2026): 60, если значение
 * делится на 60 или его нет; иначе поле остаётся в секундах — 1. Это перевод единиц, а не расчёт часов.
 */
export function inputScale(f: RuleField, v: unknown): 1 | 60 {
  if (f.unit !== 'сек' || f.locked) return 1;
  if (v === null || v === undefined) return 60;
  return typeof v === 'number' && Number.isInteger(v / 60) ? 60 : 1;
}

/** Поле так, как его видит человек: в минутах — единица «мин», пределы в минутах, дробь допустима. */
export function shownField(f: RuleField, scale: number): RuleField {
  if (scale === 1) return f;
  const out: RuleField = { ...f, unit: 'мин', control: f.control === 'integer' ? 'number' : f.control };
  if (f.min !== undefined) out.min = f.min / scale;
  if (f.max !== undefined) out.max = f.max / scale;
  return out;
}

/** Значение поля текстом для ввода: дробь — с запятой; `scale` — единица ввода (`inputScale`). */
export function rawOf(f: RuleField, v: unknown, scale = 1): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') {
    const n = scale === 1 ? v : Math.round((v / scale) * 1000) / 1000;
    return f.control === 'text' ? String(n) : String(n).replace('.', ',');
  }
  return String(v);
}

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/u;

/** «930» → «09:30», «19» → «19:00», «7:5» → «07:05»; непонятное — как есть. */
export function normalizeTime(s: string): string {
  const t = s.trim();
  const digits = /^(\d{1,2})(\d{2})$/u.exec(t);
  if (digits) return `${digits[1]?.padStart(2, '0')}:${digits[2]}`;
  const hours = /^(\d{1,2})$/u.exec(t);
  if (hours) return `${hours[1]?.padStart(2, '0')}:00`;
  const parts = /^(\d{1,2})[:.](\d{1,2})$/u.exec(t);
  if (parts) return `${parts[1]?.padStart(2, '0')}:${parts[2]?.padStart(2, '0')}`;
  return t;
}

export type Parsed = { readonly value: unknown } | { readonly error: string };

/** Введённый текст → значение поля или что исправить (текст у поля). */
export function parseTyped(f: RuleField, raw: string): Parsed {
  const s = raw.trim();
  if (s === '') return f.required ? { error: T.required } : { value: undefined };
  if (isTime(f)) {
    const t = normalizeTime(s);
    return HHMM.test(t) ? { value: t } : { error: T.timeFormat };
  }
  if (f.control === 'text') {
    if (f.pattern && typeof f.pattern === 'string' && !new RegExp(f.pattern, 'u').test(s))
      return { error: T.required };
    return { value: s };
  }
  const n = Number(s.replace(/\s/gu, '').replace(',', '.'));
  if (!Number.isFinite(n) || !/^-?[\d\s]+([.,]\d+)?$/u.test(s)) return { error: T.notNumber };
  if (f.control === 'integer' && !Number.isInteger(n)) return { error: T.notWhole };
  const unit = f.unit ?? '';
  if (f.min !== undefined && f.max !== undefined && (n < f.min || n > f.max))
    return { error: say.numberRange(f.min, f.max, unit) };
  if (f.min !== undefined && n < f.min) return { error: say.numberMin(f.min, unit) };
  if (f.max !== undefined && n > f.max) return { error: say.numberMax(f.max, unit) };
  return { value: n };
}

/**
 * Введённый текст поля в единицах ввода (`scale`, `inputScale`) → значение в единицах поля. Минуты поля в секундах
 * становятся секундами; у целого поля секунды должны получиться целыми.
 */
export function parseScaled(f: RuleField, raw: string, scale: number): Parsed {
  if (scale === 1) return parseTyped(f, raw);
  const p = parseTyped(shownField(f, scale), raw);
  if (!('value' in p) || typeof p.value !== 'number') return p;
  const v = Math.round(p.value * scale * 1000) / 1000;
  if (f.control === 'integer' && !Number.isInteger(v)) return { error: T.wholeSeconds };
  return { value: v };
}

/** Поле видно при этих значениях (`showWhen` поля своего кубика). */
export function visibleField(f: RuleField, values: Values): boolean {
  const own: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(values))
    if (k.startsWith(`${f.cube}.`)) own[k.slice(f.cube.length + 1)] = v;
  return fieldVisible(f as unknown as FormFieldView, own);
}

/** Значения, от которых строится изменение: черновик правила, иначе последняя опубликованная версия (FR-019). */
export function initialValues(base: RuleGroupView): Record<string, unknown> {
  return { ...(base.draft?.values ?? base.values) };
}

/**
 * Значения без полей, которые при значениях `by` скрыты (`showWhen`): скрытое поле — не правка, на сервер оно не идёт
 * (у «Не применять» порог и сравнение короткого визита не пишутся в черновик, T106).
 */
export function shownValues(group: RuleGroupView, values: Values, by: Values = values): Values {
  const hidden = new Set(group.fields.filter((f) => !visibleField(f, by)).map(keyOf));
  return Object.fromEntries(Object.entries(values).filter(([k]) => !hidden.has(k)));
}

/** Ключи, которые изменились. */
export function changedKeys(a: Values, b: Values): string[] {
  return [...new Set([...Object.keys(a), ...Object.keys(b)])].filter((k) => !sameValue(a[k], b[k]));
}

/**
 * Правка правила для «Сохранить в черновик», примера и влияния (`GroupEdit`: `PUT /api/rules/draft/blocks`,
 * `POST /api/rules/impact`, `POST /api/rules/day`): значения полей этого правила по ключу `кубик.поле`. Блоки
 * собирает сервер поверх базы — включение, реализацию и связку интерфейс не знает. Поля закона не идут: их задаёт
 * закон. Поля, скрытые при этих значениях (`showWhen`), не идут: в черновике остаётся значение базы.
 */
export function editValues(
  group: RuleGroupView,
  values: Values,
  fields: FieldIndex,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of group.fields) {
    const key = `${f.cube}.${f.name}`;
    const known = fields.get(key);
    if (
      known?.locked ||
      known?.unused ||
      known?.teamOnly ||
      values[key] === undefined ||
      !visibleField(f, values)
    )
      continue;
    out[key] = values[key];
  }
  return out;
}
